import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { PLAN_SETTINGS } from '../../common/constants/plan_settings';
import FeatureList from './FeatureList';
import { selectUser } from '../../slices/user/userSlice';
import MessageModal from '../../common/message-modal/MessageModal';
import Checkbox from '@mui/material/Checkbox';
import { Box } from '@mui/material';
import { NWClient } from '../../client/NWClient';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { COUPONS } from '../../common/constants/coupons';
import { sanitizeRedirectUrl } from '../../common/helpers/sanitize';
import { useErrorMessage } from '../../common/hooks/useErrorMessage';

type Plan =
  | 'free'
  | 'essentials'
  | 'growth'
  | 'apa_discounted_essentials'
  | 'free_essentials'
  | 'free_growth'
  | 'esign_only';

interface PricingCardProps {
  termsAndConditionsAgreed: boolean;
  plan: Plan;
  billingPeriod: 'monthly' | 'annual';
  discount_code?: string;
  paymentLinks: {
    [key: string]: string;
  };
}

const ModalMessageContent = (props: {
  userAgreed: boolean;
  setUserAgreed: React.SetStateAction<any>;
  plan: Plan;
}) => {
  const { t } = useTranslation();
  const { userAgreed, setUserAgreed, plan } = props;
  return (
    <>
      <h2>{t('subscriptionAgreementTitle')}</h2>
      <Box className='checkbox-row' style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Checkbox
          style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
          id='subscriptionAgreement'
          checked={userAgreed}
          onChange={(event) => {
            setUserAgreed(event.target.checked);
          }}
        />
        <label
          id={`pricingCardSubscriptionAgreementLabel_${plan}`}
          style={{ marginTop: 0 }}
          htmlFor='subscriptionAgreement'
        >
          {t('subscriptionAgreementTextPartOne')}
          <a
            id={`pricingCardBusinessKitzTermsAndConditionsLink_${plan}`}
            href='https://www.businesskitz.com/terms-and-conditions'
            target='_blank'
            rel='noreferrer'
          >
            {t('businessKitzTermsAndConditions')}
          </a>
          {plan === 'esign_only'
            ? t('subscriptionAgreementTextPartTwoEsignOnly')
            : t('subscriptionAgreementTextPartTwo')}
        </label>
      </Box>
    </>
  );
};

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingPeriod,
  discount_code,
  paymentLinks,
  termsAndConditionsAgreed,
}) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const currentUser = useAppSelector(selectUser);
  const paymentLinkParams = `?prefilled_email=${currentUser?.email}&client_reference_id=${currentUser?.user_uuid}`;
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // featured Plans = essentials plans
  const featuredPlansList = ['essentials', 'apa_discounted_essentials', 'free_essentials'];

  // Growth plans

  const growthPlansList = ['growth', 'free_growth'];

  const esignOnlyPlanList = ['esign_only'];

  const isFeatured = featuredPlansList.includes(plan);
  const planKey = `pricingPlans.${plan}`;

  const [agreementMessageVisible, setAgreementMessageVisible] = useState(false);
  const [userAgreed, setUserAgreed] = useState(false);
  const couponPrefill = discount_code ? `&prefilled_promo_code=${discount_code}` : '';

  const getPrice = () => {
    const code = discount_code ?? '';
    if (
      COUPONS.OFF_50_COUPONS.includes(code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$34.50';
    } else if (
      COUPONS.OFF_25_COUPONS.includes(code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$51.75';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$49.34';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(code) &&
      COUPONS.GROWTH_COUPONS.includes(code) &&
      (plan === 'growth' || plan === 'free_growth')
    ) {
      return 'A$92.24';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(code) &&
      COUPONS.ESIGN_ONLY_COUPONS.includes(code) &&
      plan === 'esign_only'
    ) {
      return 'A$20.74';
    } else if (
      COUPONS.OFF_50_COUPONS.includes(code) &&
      COUPONS.ESIGN_ONLY_COUPONS.includes(code) &&
      plan === 'esign_only'
    ) {
      return 'A$14.50';
    } else if (
      COUPONS.OFF_50_COUPONS.includes(code) &&
      COUPONS.GROWTH_COUPONS.includes(code) &&
      (plan === 'growth' || plan === 'free_growth')
    ) {
      return 'A$64.50';
    } else if (
      COUPONS.OFF_20_COUPONS.includes(code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$55.20';
    } else if (
      COUPONS.OFF_20_COUPONS.includes(code) &&
      COUPONS.GROWTH_COUPONS.includes(code) &&
      (plan === 'growth' || plan === 'free_growth')
    ) {
      return 'A$103.20';
    } else if (
      COUPONS.OFF_20_COUPONS.includes(code) &&
      COUPONS.ESIGN_ONLY_COUPONS.includes(code) &&
      plan === 'esign_only'
    ) {
      return 'A$23.20';
    } else if (COUPONS.OFF_100_COUPONS.includes(code)) {
      return 'A$0';
    }
    if (billingPeriod === 'annual') {
      return t(`${planKey}.priceAnnual`, { defaultValue: t(`${planKey}.price`) });
    }
    return t(`${planKey}.price`);
  };

  const getBillingPeriodText = () => {
    if (billingPeriod === 'annual') {
      return t(`${planKey}.periodAnnual`, { defaultValue: t(`${planKey}.period`) });
    }
    return t(`${planKey}.period`);
  };

  const getCtaClass = () => {
    if (featuredPlansList.includes(plan)) {
      return 'successCta';
    }
    switch (plan) {
      case 'free':
        return 'primaryCta';
      case 'esign_only':
        return 'outlineCta';
      default:
        return 'secondaryCta';
    }
  };

  const getPlanId = () => {
    if (featuredPlansList.includes(plan)) {
      return [PLAN_SETTINGS.BUSINESS_ESSENTIALS_ID, PLAN_SETTINGS.BUSINESS_ESSENTIALS_ANNUAL_ID];
    } else if (growthPlansList.includes(plan)) {
      return [PLAN_SETTINGS.BUSINESS_GROWTH_ID, PLAN_SETTINGS.BUSINESS_GROWTH_ANNUAL_ID];
    } else if (esignOnlyPlanList.includes(plan)) {
      return [PLAN_SETTINGS.ESIGN_ONLY_ID, PLAN_SETTINGS.ESIGN_ONLY_ANNUAL_ID];
    } else {
      return null;
    }
  };

  const isCurrentPlan = () => {
    if (plan === 'free') {
      return !company?.stripe_product_id;
    }
    const planIds = getPlanId();
    return planIds && planIds.includes(company?.stripe_product_id ?? '');
  };

  const isGrowthOrPremium = () => {
    return (
      company?.stripe_product_id === PLAN_SETTINGS.BUSINESS_GROWTH_ID ||
      company?.stripe_product_id === PLAN_SETTINGS.PREMIUM_ID
    );
  };

  const shouldDisableButton = () => {
    if (plan === 'free') {
      return true;
    }
    if (plan === 'growth') {
      return isCurrentPlan() || isGrowthOrPremium();
    }
    return isCurrentPlan();
  };

  const getButtonText = () => {
    if (plan === 'free') {
      return t(`${planKey}.cta`);
    }
    if (shouldDisableButton()) {
      return t('subscriptionPlanControls.started');
    }
    return t(`${planKey}.cta`);
  };

  // Determine how many features should be bold based on plan
  /*const getBoldCount = () => {
    if (featuredPlansList.includes(plan)) {
      return 4;
    } else if (growthPlansList.includes(plan)) {
      return 3;
    } else {
      return 0;
    }
  };*/

  const getFeaturesTitle = () => {
    /*if (featuredPlansList.includes(plan)) {
      return `Everything in ${t('pricingPlans.esign_only.name')}, and:`;
    } else if (growthPlansList.includes(plan)) {
      return `Everything in ${t('pricingPlans.essentials.name')}, and:`;
    } else if (plan === 'esign_only') {
      return `Everything in ${t('pricingPlans.free.name')}, and:`;
    } else {*/
    return 'Key Features:';
    //}
  };

  return (
    <>
      <div
        className={`priceCard ${isFeatured ? 'featuredCard' : ''} ${esignOnlyPlanList.includes(plan) ? 'esignCard' : ''} ${growthPlansList.includes(plan) ? 'growthCard' : ''}`}
      >
        {(isFeatured || esignOnlyPlanList.includes(plan)) && (
          <span className='popularBadge'>{t(`${planKey}.badge`)}</span>
        )}
        <h2 className='planName'>{t(`${planKey}.name`)}</h2>
        <div className='alignDesc'>
          <p className='planTagline'>{t(`${planKey}.tagline`)}</p>
        </div>

        <div className='priceSection alignPrice'>
          <span className='price'>{getPrice()}</span>
          <span className='billingPeriod'>
            {getBillingPeriodText()}
            {discount_code === 'ESSA35' &&
            (plan === 'essentials' || plan === 'growth' || plan === 'esign_only')
              ? ' (' + t('planPageMiscellaneous.includingGST') + ')'
              : ''}
          </span>
        </div>

        <div className='ctaSection'>
          {shouldDisableButton() ? (
            <button
              id={`pricingCardCtaDisabledBtn_${plan}`}
              disabled
              className={`ctaButton ${getCtaClass()}`}
            >
              {getButtonText()}
            </button>
          ) : (
            <>
              {!termsAndConditionsAgreed ? (
                <button
                  id={`pricingCardCtaAgreeTermsBtn_${plan}`}
                  className={`ctaButton ${getCtaClass()}`}
                  onClick={
                    company
                      ? () => setAgreementMessageVisible(true)
                      : () =>
                          navigate(
                            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                          )
                  }
                >
                  {getButtonText()}
                </button>
              ) : (
                <a
                  id={`pricingCardCtaCheckoutLink_${plan}`}
                  href={
                    company
                      ? paymentLinks[plan] + paymentLinkParams + couponPrefill
                      : `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                  }
                  className={`ctaButton ${getCtaClass()}`}
                  rel='noreferrer'
                >
                  {getButtonText()}
                </a>
              )}
            </>
          )}
          <p className='ctaSubtext'>{t(`${planKey}.ctaSubtext`)}</p>
        </div>

        <div className='featuresSection'>
          <div className='alignHead'>
            <h3 className='featuresTitle'>{getFeaturesTitle()}</h3>
          </div>
          <div className='featureListWrapper'>
            <FeatureList
              features={t(`${planKey}.features`, { returnObjects: true }) as string[]}
              boldCount={0 /*getBoldCount()*/}
            />
          </div>
        </div>
        {agreementMessageVisible && (
          <MessageModal
            open={agreementMessageVisible}
            modalContent={
              <ModalMessageContent
                plan={plan}
                userAgreed={userAgreed}
                setUserAgreed={setUserAgreed}
              />
            }
            actionButtonText={t('buttons.continue')}
            actionButtonDisabled={!userAgreed}
            actionButtonFn={() => {
              if (!termsAndConditionsAgreed) {
                dispatch(showOverlay());
                NWClient.subscriptionTermsAccept()
                  .then(() => {
                    window.open(paymentLinks[plan] + paymentLinkParams + couponPrefill, '_self');
                    setAgreementMessageVisible(false);
                  })
                  .catch((error) => {
                    toast.error(getErrorMessage(error));
                  })
                  .finally(() => {
                    dispatch(hideOverlay());
                  });
              } else {
                window.open(paymentLinks[plan] + paymentLinkParams + couponPrefill, '_self');
              }
            }}
            goBackButtonText={t('buttons.goBack')}
            onClose={() => {
              setAgreementMessageVisible(false);
            }}
          />
        )}
      </div>
    </>
  );
};

export default PricingCard;
